import type { FilesStorageSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/files_storages';
import type {
  FilesFileSchemaWithId,
  FilesFileSetSchemaWithIdNullable,
  FilesFormatSchemaWithId,
  FilesFormatSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/files';
import type { Entities } from '@iconik-io/shared-lib/src/redux/entities/types';
import { CLOUD_STORAGE_METHODS } from '@iconik-io/shared-lib/src/utils/constants/storages';
import type { IconName } from '@iconik-io/uad';

import type { FileStatus } from '../../constants/files';
import VARIANTS, { type VariantType } from '../../constants/variants';

export type LabelType = { value: string; variant: VariantType };

export const getStorageIcon = (
  storage: FilesStorageSchemaPatched | undefined,
) => {
  let icon: IconName = 'storage_on_premise';
  if (!storage) {
    return icon;
  }
  if (storage.purpose === 'ARCHIVE') {
    icon = 'archive_archived';
  } else if (CLOUD_STORAGE_METHODS.includes(storage.method)) {
    icon = 'storage_cloud';
  }
  return icon;
};

export const getFileSetOrFormatStatus = (
  format: FilesFormatSchemaWithIdNullable | undefined,
  fileSet: FilesFileSetSchemaWithIdNullable | undefined,
): LabelType | undefined => {
  if (format?.archive_status === 'ARCHIVING') {
    return { variant: VARIANTS.WARNING, value: format.archive_status };
  }
  if (format?.archive_status === 'FAILED_TO_ARCHIVE') {
    return { variant: VARIANTS.DANGER, value: format.archive_status };
  }
  if (fileSet?.status === 'DELETED') {
    return { variant: VARIANTS.DANGER, value: fileSet.status };
  }
  if (fileSet?.is_archive) {
    return { variant: VARIANTS.WARNING, value: 'ARCHIVED' };
  }
  return undefined;
};

export const getRowStatus = (
  format: FilesFormatSchemaWithIdNullable | undefined,
  files: FilesFileSchemaWithId[] | undefined,
  fileSet: FilesFileSetSchemaWithIdNullable | undefined,
): LabelType | undefined => {
  if (!files) {
    return undefined;
  }
  const fileSetOrFormatStatus = getFileSetOrFormatStatus(format, fileSet);
  if (fileSetOrFormatStatus) {
    return fileSetOrFormatStatus;
  }

  const relevantFile = files.find((file) => file.status !== 'CLOSED');
  if (relevantFile && relevantFile.status) {
    return { variant: VARIANTS.DANGER, value: relevantFile.status };
  }
  return undefined;
};

const conflictStatuses = new Set<FileStatus | undefined>([
  'OPEN',
  'GROWING',
  'AWAITED',
  'FAILED',
  'MISSING',
  'DELETED',
]);

export const isFileStatusConflict = (
  format: FilesFormatSchemaWithIdNullable | undefined,
  files: FilesFileSchemaWithId[] | undefined,
  fileSet: FilesFileSetSchemaWithIdNullable | undefined,
): boolean => {
  const isFileSetOrFormatBadStatus = Boolean(
    getFileSetOrFormatStatus(format, fileSet),
  );

  if (!files) {
    return false;
  }
  const isFileBadStatus = files.some((file) =>
    conflictStatuses.has(file.status),
  );
  return isFileSetOrFormatBadStatus && isFileBadStatus;
};

export const getPathDisplay = (path?: string): string => {
  if (!path) {
    return '';
  }
  if (path.startsWith('/')) {
    return path.replace('/', '');
  }
  if (path) {
    return `/${path}`;
  }
  return '';
};

export const canDownloadFormat = (
  formatId: string,
  canDownloadRole: boolean | undefined,
  formatFiles: FilesFileSchemaWithId[] = [],
  formatFilesets: FilesFileSetSchemaWithIdNullable[] = [],
): boolean => {
  if (!canDownloadRole || !formatFiles.length || !formatFilesets.length) {
    return false;
  }
  const nonArchivedFilesets = formatFilesets
    .filter((fileSet) => fileSet.format_id === formatId && !fileSet.is_archive)
    .map((fileSet) => fileSet.id);
  return Boolean(
    formatFiles.find(
      (file) =>
        file.url &&
        file.status === 'CLOSED' &&
        nonArchivedFilesets.includes(file.file_set_id),
    ),
  );
};

export const canDownloadFileSet = (
  fileSet: FilesFileSetSchemaWithIdNullable,
  canDownloadRole: boolean | undefined,
  fileSetFiles: FilesFileSchemaWithId[] = [],
): boolean => {
  if (!canDownloadRole || !fileSetFiles.length || fileSet.is_archive) {
    return false;
  }

  if (!fileSetFiles.find((file) => Boolean(file.url))) {
    return false;
  }
  return Boolean(
    fileSetFiles.find((file) => file.url && file.status === 'CLOSED'),
  );
};

export const getFileSetsIsgFileId = (
  format: FilesFormatSchemaWithId,
  fileSet: FilesFileSetSchemaWithIdNullable,
  fileSetFiles: FilesFileSchemaWithId[] = [],
  storages: Entities<FilesStorageSchemaPatched> = {},
): string | undefined => {
  if (
    fileSetFiles.length &&
    fileSet.storage_id &&
    (storages[fileSet.storage_id]?.method === 'FILE' ||
      format.storage_methods?.includes('FILE')) &&
    fileSetFiles.find(
      (file) =>
        !file.url && (file.status === 'CLOSED' || file.status === 'GROWING'),
    )
  ) {
    return fileSetFiles[0].id;
  }
  return undefined;
};
